<template>
  <transition name="play-alert-fade">
    <div
      v-if="show"
      class="play-alert"
      :class="`play-alert--${variant}`"
      role="alert"
    >
      <span
        v-if="$slots.icon"
        class="play-alert__icon"
      ><slot name="icon" /></span>

      <div class="play-alert__body">
        <slot />
      </div>

      <button
        v-if="dismissible"
        type="button"
        class="play-alert__close"
        aria-label="Close"
        @click="$emit('dismissed')"
      >&times;</button>
    </div>
  </transition>
</template>

<script>
/**
 * PlayAlert — inline notice for the Play system component layer.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-alert>. A soft role-tinted background + a solid role
 * border keep it readable but quiet (dashboard dosage). Toggle with `show`;
 * default slot holds the content (an `.alert-heading` / heading gets the
 * display font). Set `dismissible` to render a close button that emits
 * `dismissed`.
 *
 * VUE3-MIGRATION: props/slots are already Vue 3-safe — no Vue 2-only constructs.
 */
export default {
  name: 'PlayAlert',
  props: {
    /** Role tint: primary, secondary (violet), highlight (lime), accent, danger */
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent', 'danger'].includes(v),
    },
    show: {
      type: Boolean,
      default: true,
    },
    dismissible: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.play-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-family: var(--font-body);
  font-weight: 500;
  color: var(--text-strong);
  border: var(--border-w) solid;
  border-radius: var(--radius);

  &__body { flex: 1; min-width: 0; }
  &__icon { display: inline-flex; align-items: center; font-size: 1.2em; }

  &__close {
    flex: none;
    padding: 0;
    background: none;
    border: none;
    font-size: 22px;
    line-height: 1;
    color: inherit;
    cursor: pointer;
    opacity: .55;
    transition: opacity var(--dur-fast) var(--ease-out);
    &:hover { opacity: 1; }
  }

  /* Headings inside the slot adopt the display face. */
  ::v-deep .alert-heading,
  ::v-deep h1, ::v-deep h2, ::v-deep h3,
  ::v-deep h4, ::v-deep h5, ::v-deep h6 {
    font-family: var(--font-display);
    font-weight: 800;
    margin-bottom: 4px;
    letter-spacing: -0.01em;
  }

  /* Role variants — soft tint + solid border. */
  @each $name in primary, secondary, highlight, accent, danger {
    &--#{$name} {
      background: var(--c-#{$name}-soft);
      border-color: var(--c-#{$name});
    }
  }
}

.play-alert-fade-enter-active,
.play-alert-fade-leave-active {
  transition: opacity var(--dur) var(--ease-out), transform var(--dur) var(--ease-bounce);
}
.play-alert-fade-enter,
.play-alert-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
